import { cn } from '@ais-ui/utils';

interface TreeGuideProps {
  isFirst: boolean;
  isLast: boolean;
  className?: string;
}

/**
 * SVG-based tree connector with soft rounded corners.
 * Inspired by Josh Comeau's course navigation tree pattern.
 *
 * - Non-last items: semicircle bump that arcs out to content and back to the stem
 * - Last item: quarter-circle that arcs out and stays horizontal (no return)
 * - First child: stem extends upward into the parent row via overflow-visible
 *
 * Row height = h-9 (36px), width = w-5 (20px).
 */
export const TreeGuide = ({ isFirst, isLast, className }: TreeGuideProps) => {
  const radius = 6;
  const branchY = 18; // vertical center
  const curveStartY = branchY - radius; // 12
  const curveEndY = branchY + radius; // 24
  const curveEndX = 1 + radius; // 7

  // Vertical line extent — gap where the semicircle bump is (y=12 to y=24)
  const startY = isFirst ? -12 : 0;

  // For non-last: draw vertical in two segments, skipping the bump zone
  // For last: just draw to the curve start
  const verticalSegments: Array<{ y1: number; y2: number }> = isLast
    ? [{ y1: startY, y2: curveStartY }]
    : [
        { y1: startY, y2: curveStartY },
        { y1: curveEndY, y2: 36 },
      ];

  // Non-last: semicircle bump (out and back) + horizontal from apex
  // Last: quarter circle (out only) + horizontal from endpoint
  const branchPath = isLast
    ? `M 1 ${curveStartY} A ${radius} ${radius} 0 0 1 ${curveEndX} ${branchY} H 16`
    : `M 1 ${curveStartY} A ${radius} ${radius} 0 0 1 ${curveEndX} ${branchY} A ${radius} ${radius} 0 0 1 1 ${curveEndY}`;

  return (
    <div
      className={cn('relative w-5 flex-shrink-0 self-stretch', className)}
      aria-hidden="true"
    >
      <svg
        className="absolute inset-0 w-full h-full overflow-visible text-muted-foreground"
        viewBox="0 0 20 36"
        fill="none"
      >
        {/* Vertical stem — split to avoid overlap with semicircle bump */}
        {verticalSegments.map((seg, i) => (
          <line
            key={i}
            x1="1"
            y1={seg.y1}
            x2="1"
            y2={seg.y2}
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        ))}
        {/* Branch arc */}
        <path
          d={branchPath}
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        {/* Horizontal connector to content (non-last only — last item path includes H 16) */}
        {!isLast && (
          <line
            x1={curveEndX}
            y1={branchY}
            x2="16"
            y2={branchY}
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        )}
      </svg>
    </div>
  );
};
